/**
 * 軌跡（trail）の描画。直近30分の点列を線で描く。
 *
 * design §16-3 / 要件 1-4, 7-1 に対応。
 * trail_visible=true のユーザーのみ（サーバーが trail を返すのはそのユーザーだけ）。
 * MapLibre の GeoJSON ソース＋line レイヤーを更新する命令的ユーティリティ。
 */
import type {
  ExpressionSpecification,
  GeoJSONSource,
  Map as MaplibreMap,
} from 'maplibre-gl';
import type { UserLocation } from '../api/client';

const SOURCE_ID = 'trails';
const LAYER_ID = 'trails-line';

type TrailFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.LineString>;

/** users から軌跡の FeatureCollection を作る（点が2つ以上あるものだけ）。 */
function toTrailFeatures(users: UserLocation[]): TrailFeatureCollection {
  const features = users
    .filter((u) => u.trail.length >= 2)
    .map<GeoJSON.Feature<GeoJSON.LineString>>((u) => ({
      type: 'Feature',
      properties: { user_id: u.user_id },
      geometry: {
        type: 'LineString',
        coordinates: u.trail.map((p) => [p.lng, p.lat]),
      },
    }));
  return { type: 'FeatureCollection', features };
}

/** 軌跡ソース/レイヤーを（無ければ）初期化する。map load 後に一度呼ぶ。 */
export function ensureTrailLayer(map: MaplibreMap): void {
  if (map.getSource(SOURCE_ID)) {
    return;
  }
  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#3182ce',
      'line-width': 3,
      'line-opacity': 0.7,
    },
  });
}

/**
 * 選択状態に応じて軌跡レイヤーの見た目を更新する。
 * - 選択なし: 全軌跡を通常表示（不透明度0.7・幅3）
 * - 選択あり: 選択ユーザーを濃く・太く、他は薄く細く（埋もれないように）
 * feature の properties.user_id を使った data-driven styling。
 */
function applyTrailStyle(map: MaplibreMap, selectedUserId: string | null): void {
  if (!map.getLayer(LAYER_ID)) {
    return;
  }
  if (!selectedUserId) {
    map.setPaintProperty(LAYER_ID, 'line-opacity', 0.7);
    map.setPaintProperty(LAYER_ID, 'line-width', 3);
    map.setPaintProperty(LAYER_ID, 'line-color', '#3182ce');
    return;
  }
  // 選択中かどうかの式。data-driven styling（feature の user_id と比較）。
  const sel = selectedUserId;
  const opacityExpr = [
    'case',
    ['==', ['get', 'user_id'], sel],
    0.9,
    0.15,
  ] as unknown as ExpressionSpecification;
  const widthExpr = [
    'case',
    ['==', ['get', 'user_id'], sel],
    4,
    2,
  ] as unknown as ExpressionSpecification;
  const colorExpr = [
    'case',
    ['==', ['get', 'user_id'], sel],
    '#3182ce',
    '#90cdf4',
  ] as unknown as ExpressionSpecification;
  map.setPaintProperty(LAYER_ID, 'line-opacity', opacityExpr);
  map.setPaintProperty(LAYER_ID, 'line-width', widthExpr);
  map.setPaintProperty(LAYER_ID, 'line-color', colorExpr);
}

/**
 * 軌跡データを更新する。ポーリングのたびに呼ぶ。
 * @param selectedUserId 選択中のユーザー（アコーディオンで選択）。濃淡で強調する。
 */
export function updateTrails(
  map: MaplibreMap,
  users: UserLocation[],
  selectedUserId: string | null = null,
): void {
  const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
  if (!source) {
    return;
  }
  source.setData(toTrailFeatures(users));
  applyTrailStyle(map, selectedUserId);
}
